<script lang="ts" setup>
import SkrButton from '@/components/button/SkrButton.vue'

interface DocumentData {
  id: string
  title: string
  pages: number
  width: number
  height: number
  offset: number
}

const props = defineProps<{
  signatureRequest: SignatureRequestData | null
}>()

const { directDocumentRepository } = useLegacyApi()
const { logError } = useErrorHandling()
const { getPagePreview } = useDocumentHelpers()
const documents = ref<DocumentData[]>([])
const pageUrlStates = ref<('initial' | 'loading' | 'placeholder' | `blob:${string}`)[]>([])
const pageRefs = ref<(HTMLElement | null)[]>([])
const viewer = defineModel<boolean>('visible', { required: true })

const getDocuments = async () => {
  const documents: DocumentData[] = []
  const srDocuments = props.signatureRequest?.documents ?? []
  let pageOffset = 0
  for await (const document of srDocuments) {
    const versionId = document.versionId
    const documentData = await directDocumentRepository.getDocument(versionId)
    documents.push({
      id: versionId,
      title: documentData.title,
      pages: documentData.pageCount,
      width: documentData.pageWidth,
      height: documentData.pageHeight,
      offset: pageOffset,
    })
    pageOffset += documentData.pageCount
    for (let page = 0; page < documentData.pageCount; page++) {
      pageUrlStates.value.push('initial')
      pageRefs.value.push(null)
    }
  }

  return documents
}
watchEffect(() => {
  if (props.signatureRequest) {
    getDocuments().then(docs => {
      documents.value = docs
    })
  }
})

const pagePreviewAbortController = new AbortController()
onUnmounted(() => {
  pagePreviewAbortController.abort()
})
const getPageUrl = async (docIdx: number, page: number) => {
  try {
    // Pages are zero-indexed on the backend side
    const doc = documents.value[docIdx]
    const blob = await getPagePreview(doc.id, page, 100, pagePreviewAbortController.signal)

    const url = URL.createObjectURL(blob) as `blob:${string}`
    pageUrlStates.value[doc.offset + page] = url
  } catch (error) {
    if (isAbortError(error)) {
      console.debug('Aborted page preview request')
    } else {
      logError('Failed to load document preview', error)
    }
    pageUrlStates.value[page] = 'placeholder'
  }
}

const documentArea = ref<HTMLElement | null>(null)
const fullVisibilityObserver = shallowRef<IntersectionObserver>()

const setPageRef = (index: number, el: Element | globalThis.ComponentPublicInstance | null) => {
  if (el instanceof HTMLElement) {
    pageRefs.value[index] = el
    fullVisibilityObserver.value?.observe(el)
  } else {
    pageRefs.value[index] = null
  }
}

onMounted(() => {
  fullVisibilityObserver.value = new IntersectionObserver(
    entries => {
      for (const entry of entries) {
        if (entry.intersectionRatio >= 0.25) {
          if (entry.target instanceof HTMLElement) {
            const doc = parseInt(entry.target.dataset.doc ?? '0', 10)
            const pageNumber = parseInt(entry.target.dataset.page ?? '0', 10)
            const offset = documents.value[doc].offset ?? 0
            if (pageUrlStates.value[offset + pageNumber] === 'initial') {
              pageUrlStates.value[offset + pageNumber] = 'loading'
              getPageUrl(doc, pageNumber)
            }
          }
        }
      }
    },
    { root: documentArea.value, threshold: [0, 0.25, 0.5, 0.75, 1] }
  )
})

onUnmounted(() => {
  fullVisibilityObserver.value?.disconnect()
  for (const page of pageRefs.value) {
    if (page) {
      fullVisibilityObserver.value?.unobserve(page)
    }
  }
  for (const page of pageUrlStates.value) {
    if (page.startsWith('blob:')) {
      URL.revokeObjectURL(page)
    }
  }
})
</script>

<template>
  <v-overlay
    v-model="viewer"
    class="overflow-auto"
    content-class="w-100"
    data-cy="overlay_direct_sign_viewer"
    :close-on-content-click="false"
    :scroll-strategy="'none'"
  >
    <div class="direct-sign-viewer__close">
      <div class="direct-sign-viewer__close__btn">
        <skr-button
          icon="custom:e_remove"
          color="white"
          data-cy="overlay_direct_sign_viewer_close"
          @click="viewer = false"
        />
      </div>
    </div>
    <div ref="documentArea" class="d-flex flex-column align-center w-full pb-12">
      <div
        v-for="(doc, docIdx) in documents"
        :key="doc.id"
        class="d-flex flex-column align-center pb-12 position-relative"
      >
        <div
          v-for="(_, page) of doc.pages"
          :key="page"
          :ref="el => setPageRef(doc.offset + page, el)"
          :data-doc="docIdx"
          :data-page="page"
          class="direct-sign-viewer__page pb-8"
        >
          <v-skeleton-loader
            v-if="['loading', 'initial'].includes(pageUrlStates[doc.offset + page])"
            type="image"
            width="866px"
            height="100vh"
          />
          <div
            v-else-if="pageUrlStates[doc.offset + page] === 'placeholder'"
            class="bg-white h-screen w-100 d-flex justify-center align-center rounded-lg"
          >
            <nuxt-img src="/img/error-404.webp" alt="placeholder" class="w-50" />
          </div>
          <img
            v-else
            class="direct-sign-viewer__page__img"
            :src="pageUrlStates[doc.offset + page]"
            :alt="`Document ${doc.title} page ${page + 1}`"
          />
        </div>
      </div>
    </div>
  </v-overlay>
</template>

<style lang="scss" scoped>
.direct-sign-viewer {
  &__close {
    position: sticky;
    top: 0px;
    left: 0px;
    z-index: 9999;
    width: 100%;
    height: 48px;
    &__btn {
      width: 100%;
      max-width: 982px;
      display: flex;
      justify-content: flex-end;
      position: absolute;
      top: 48px;
      transform: translateX(-50%);
      margin-left: 50%;
    }
  }
  &__page {
    max-width: 866px;
    :deep(.v-skeleton-loader) {
      background: #fff1;
    }
    :deep(.v-skeleton-loader__image) {
      width: 100%;
      height: 100%;
    }
    &__img {
      max-width: 100%;
      height: auto;
      display: block;
      box-shadow: 0px 2.946px 6.629px 0px rgba(40, 62, 102, 0.19);
      border-radius: 8px;
    }
  }
}
</style>
